import React, { useEffect, useRef, useState } from "react";
import { X, Sparkles, Send, MessageSquareDashed, Slash } from "lucide-react";
import api from "../lib/api";

const SUGGESTIONS = [
  "Why did our Scope 2 emissions spike last quarter?",
  "Draft a narrative for the IFRS S2 climate metrics section.",
  "Which KPIs are currently the highest risk to disclosure?",
  "Explain the standards alignment for our privacy KPI.",
];

export default function AdviserDrawer({ open, onClose, kpiId = null, anomalyId = null }) {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [commands, setCommands] = useState([]);
  const [followups, setFollowups] = useState([]);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const msgIdRef = useRef(0);
  const nextMsgId = () => ++msgIdRef.current;

  useEffect(() => {
    if (open) {
      api.get("/adviser/commands").then(r => setCommands(r.data.commands || [])).catch(()=>{});
      if (messages.length === 0) {
        setMessages([{
          id: nextMsgId(),
          role: "assistant",
          text:
            "Hi — I'm your ESG Adviser. Try a slash command (type **/** to see options) " +
            "or ask anything about your tenant data. I cite IFRS S1/S2, GRI, SASB, GHG Protocol and ISO.",
        }]);
      }
    }
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const showPalette = input.startsWith("/");
  const filteredCommands = commands.filter(c => c.cmd.startsWith(input.split(" ")[0] || "/"));

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setMessages(m => [...m, { id: nextMsgId(), role: "user", text: msg }]);
    setInput(""); setBusy(true); setFollowups([]);
    try {
      const ctx = {};
      if (kpiId) ctx.kpi_id = kpiId;
      if (anomalyId) ctx.anomaly_id = anomalyId;
      const r = await api.post("/adviser/chat", { message: msg, context: ctx, session_id: sessionId });
      setSessionId(r.data.session_id);
      setMessages(m => [...m, { id: nextMsgId(), role: "assistant", text: r.data.reply }]);
      setFollowups(r.data.followups || []);
    } catch (e) {
      setMessages(m => [...m, { id: nextMsgId(), role: "assistant", text: "Sorry — the adviser is unavailable right now." }]);
    } finally { setBusy(false); }
  };

  const insertCommand = (cmd) => {
    setInput(cmd.cmd + (cmd.args ? " " : ""));
    inputRef.current?.focus();
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} data-testid="adviser-backdrop" />
      <aside className="fixed right-0 top-0 bottom-0 w-[460px] bg-white border-l border-border z-50 flex flex-col">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-md bg-brand text-white flex items-center justify-center">
              <Sparkles size={14} />
            </div>
            <div className="leading-tight">
              <div className="text-sm font-semibold">ESG Adviser</div>
              <div className="text-[11px] text-ink-soft">Claude Sonnet 4.5 · slash commands enabled</div>
            </div>
          </div>
          <button data-testid="adviser-close-button" onClick={onClose} className="btn btn-ghost"><X size={16}/></button>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="adviser-messages">
          {messages.map((m) => (
            <div key={m.id} className={"flex " + (m.role === "user" ? "justify-end" : "justify-start")}>
              <div className={
                "max-w-[88%] text-sm rounded-md px-3 py-2 whitespace-pre-wrap " +
                (m.role === "user" ? "bg-brand text-white" : "card bg-surface")
              }>{m.text}</div>
            </div>
          ))}
          {busy && (
            <div className="flex justify-start">
              <div className="card bg-surface px-3 py-2 text-sm text-ink-soft flex items-center gap-2">
                <MessageSquareDashed size={14} /> thinking…
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-border space-y-2">
          {/* Followups (after a slash response) */}
          {followups.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="adviser-followups">
              <span className="dlabel">Try next</span>
              {followups.map((f, i) => (
                <button
                  key={f}
                  data-testid={`adviser-followup-${i}`}
                  onClick={() => send(f)}
                  disabled={busy}
                  className="pill text-[11px]"
                >{f}</button>
              ))}
            </div>
          )}

          {/* Default suggestions (when no slash) */}
          {!showPalette && followups.length === 0 && (
            <div className="flex flex-wrap gap-2">
              {SUGGESTIONS.map((p, i) => (
                <button
                  key={p}
                  data-testid={`adviser-prompt-${i}`}
                  onClick={() => send(p)}
                  disabled={busy}
                  className="pill text-[11px]"
                >{p}</button>
              ))}
            </div>
          )}

          {/* Slash command palette */}
          {showPalette && filteredCommands.length > 0 && (
            <div className="card overflow-hidden" data-testid="adviser-palette">
              <div className="px-3 py-1.5 dlabel border-b border-border flex items-center gap-1">
                <Slash size={11}/> Commands
              </div>
              <ul className="max-h-44 overflow-y-auto">
                {filteredCommands.map((c) => (
                  <li key={c.cmd}>
                    <button
                      data-testid={`palette-${c.cmd.replace('/', '')}`}
                      type="button"
                      onClick={() => insertCommand(c)}
                      className="w-full text-left px-3 py-1.5 hover:bg-surface flex items-baseline gap-2"
                    >
                      <span className="font-mono text-xs text-brand">{c.cmd}</span>
                      <span className="font-mono text-[11px] text-ink-soft">{c.args}</span>
                      <span className="text-[11px] text-ink-soft truncate">— {c.desc}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex gap-2">
            <input
              ref={inputRef}
              data-testid="adviser-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter") send();
                // Tab autocompletes the first matching slash command
                if (e.key === "Tab" && showPalette && filteredCommands[0]) {
                  e.preventDefault();
                  insertCommand(filteredCommands[0]);
                }
              }}
              placeholder="Try /help or ask about a KPI…"
              className="flex-1 border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-brand font-mono"
            />
            <button
              data-testid="adviser-send-button"
              onClick={() => send()}
              disabled={busy || !input.trim()}
              className="btn btn-primary"
            ><Send size={14}/></button>
          </div>
        </div>
      </aside>
    </>
  );
}
